// src/components/Enrollments/components/EnrollmentsTable.jsx
// ============================================================================
// BLOCK ENR.TBL.0 — Enrollments report table
// ----------------------------------------------------------------------------
// PURPOSE
//  - Render the old GridView report as an MUI table with a sticky header.
//  - Show server-computed monthly totals in the footer.
// ============================================================================

import React from 'react';

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { COLUMN_DEFINITIONS } from '../constants/enrollmentsUi.js';
import EnrollmentsRow from './EnrollmentsRow.jsx';

function getFooterCellValue(columnKey, totals) {
  if (columnKey === 'facility') {
    return 'Totals';
  }

  if (columnKey.startsWith('m') || columnKey === 'total') {
    return Number(totals?.[columnKey] || 0).toLocaleString();
  }

  return '';
}

function EnrollmentsTable({ rows, totals }) {
  if (!rows.length) {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography
          variant="h6"
          align="center"
          sx={{ color: 'text.secondary' }}
          data-cy="enrollments-empty-state"
        >
          No enrollments found for the selected rep and year.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%' }}>
      <TableContainer sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small" data-cy="enrollments-table">
          <TableHead>
            <TableRow>
              {COLUMN_DEFINITIONS.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{ minWidth: column.minWidth, whiteSpace: 'nowrap', fontWeight: 700 }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, index) => (
              <EnrollmentsRow
                key={`${row.division}-${row.facility}-${row.patient_type}-${row.state}-${row.year}-${index}`}
                row={row}
              />
            ))}
          </TableBody>

          <TableFooter>
            <TableRow>
              {COLUMN_DEFINITIONS.map((column) => (
                <TableCell
                  key={`footer-${column.key}`}
                  align={column.align}
                  sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}
                >
                  {getFooterCellValue(column.key, totals)}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default React.memo(EnrollmentsTable);
